import {
  Component,
  OnInit,
  Input,
  EventEmitter,
  Output,
  OnChanges,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core'
import { AiResponderSettings } from '../../store/ai-settings.model'
import { InquiryService } from '@tv3/store/inquiry/inquiry.service'
import {
  Destroyable,
  Rental,
  selectRentalEntities,
  untilDestroy,
  selectAllRentals,
} from '@tokeet-frontend/tv3-platform'
import { Store, select } from '@ngrx/store'
import { Inquiry } from '@tv3/store/inquiry/inquiry.model'
import { Guest } from '@tv3/store/guest/guest.model'
import { GuestService } from '@tv3/store/guest/guest.service'
import * as lodash from 'lodash'
import { UntypedFormControl, Validators } from '@angular/forms'
import { debounceTime, observeOn } from 'rxjs/operators'
import { SearchService } from '@tv3/store/search/search.service'
import { BehaviorSubject, asyncScheduler } from 'rxjs'
import { InquirySearchResult } from '@tv3/store/search/search-result.model'
import { SearchGuests } from '@tv3/store/guest/guest.actions'
import { selectAllGuests } from '@tv3/store/guest/guest.selectors'
import { MatPaginator } from '@angular/material/paginator'
import { MatTableDataSource } from '@angular/material/table'
import { MatSort } from '@angular/material/sort'
import { UpdateAISettings } from '../../store/ai-settings.actions'
import { AmplitudeService } from '@tv3/services/amplitude.service'

@Component({
  selector: 'app-ai-responder-ignore-list',
  templateUrl: './ai-responder-ignore-list.component.html',
  styleUrls: ['./ai-responder-ignore-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class AiResponderIgnoreListComponent extends Destroyable implements OnInit, OnChanges {
  @Input() settings: AiResponderSettings

  @ViewChild('bookingsPaginator') set bookingsPaginator(p: MatPaginator) {
    this.bookingsDataSource.paginator = p
  }
  @ViewChild('bookingsSort') set bookingsSort(s: MatSort) {
    this.bookingsDataSource.sort = s
  }
  bookingsColumns = ['name', 'email', 'guestArrive', 'guestDepart', 'actions']
  bookingsDataSource = new MatTableDataSource<Inquiry>()

  @ViewChild('guestsPaginator') set guestsPaginator(p: MatPaginator) {
    this.guestsDataSource.paginator = p
  }
  @ViewChild('guestsSort') set guestsSort(s: MatSort) {
    this.guestsDataSource.sort = s
  }
  guestsColumns = ['name', 'primaryEmail', 'actions']
  guestsDataSource = new MatTableDataSource<Guest>()

  @ViewChild('regexpsPaginator') set regexpsPaginator(p: MatPaginator) {
    this.regexpsDataSource.paginator = p
  }
  @ViewChild('regexpsSort') set regexpsSort(s: MatSort) {
    this.regexpsDataSource.sort = s
  }
  regexpsColumns = ['content', 'actions']
  regexpsDataSource = new MatTableDataSource<string>()

  @ViewChild('rentalsPaginator') set rentalsPaginator(p: MatPaginator) {
    this.rentalsDataSource.paginator = p
  }
  @ViewChild('rentalsSort') set rentalsSort(s: MatSort) {
    this.rentalsDataSource.sort = s
  }
  rentalsColumns = ['name', 'email', 'actions']
  rentalsDataSource = new MatTableDataSource<Rental>()

  bookingsCtrl = new UntypedFormControl([], [Validators.required])
  bookingSearchTerm$ = new BehaviorSubject<string>('')
  inquiries$ = new BehaviorSubject<InquirySearchResult[]>([])

  ignoreGuestsRegexpCtrl = new UntypedFormControl('', [Validators.required])

  guestsCtrl = new UntypedFormControl([], [Validators.required])
  guestSearchTerm$ = new BehaviorSubject<string>('')
  guests$ = this.store.pipe(select(selectAllGuests))

  rentalsCtrl = new UntypedFormControl([], [Validators.required])
  rentals$ = this.store.pipe(observeOn(asyncScheduler), select(selectAllRentals))

  searchBookingsCtrl = new UntypedFormControl()
  searchGuestsCtrl = new UntypedFormControl()
  searchGuestRegexpsCtrl = new UntypedFormControl()
  searchRentalsCtrl = new UntypedFormControl()

  constructor(
    private store: Store<any>,
    private inquiryService: InquiryService,
    private guestService: GuestService,
    private searchService: SearchService,
    private amplitudeService: AmplitudeService
  ) {
    super()
  }

  ngOnInit(): void {
    this.searchService
      .getInquiries(this.bookingSearchTerm$)
      .pipe(untilDestroy(this))
      .subscribe((inquiries) => {
        this.inquiries$.next(inquiries)
      })
    this.guestSearchTerm$.pipe(debounceTime(250), untilDestroy(this)).subscribe((term) => {
      this.store.dispatch(SearchGuests({ term }))
    })
    this.regexpsDataSource.filterPredicate = (item: string, filter: string) => {
      return lodash.toLower(item).includes(lodash.toLower(filter))
    }
    this.regexpsDataSource.sortingDataAccessor = (item: string, property: string) => {
      return lodash.toLower(item)
    }

    this.bookingsDataSource.sortingDataAccessor = (item: Inquiry, property: string) => {
      switch (property) {
        case 'name':
          return lodash.toLower(item.guestDetails?.name || '')
        case 'email':
          return lodash.toLower(item.guestDetails?.email || '')
        default:
          return item[property]
      }
    }

    this.searchBookingsCtrl.valueChanges
      .pipe(untilDestroy(this))
      .subscribe((term) => (this.bookingsDataSource.filter = term))
    this.searchGuestRegexpsCtrl.valueChanges
      .pipe(untilDestroy(this))
      .subscribe((term) => (this.regexpsDataSource.filter = term))
    this.searchGuestsCtrl.valueChanges
      .pipe(untilDestroy(this))
      .subscribe((term) => (this.guestsDataSource.filter = term))
    this.searchRentalsCtrl.valueChanges
      .pipe(untilDestroy(this))
      .subscribe((term) => (this.rentalsDataSource.filter = term))
  }

  refresh() {
    this.bookingsCtrl.reset()
    this.guestsCtrl.reset()
    this.rentalsCtrl.reset()
    this.ignoreGuestsRegexpCtrl.reset()

    this.regexpsDataSource.data = this.settings.ignoreGuestsRegexp || []

    this.inquiryService.get(this.settings.ignoreBookings).subscribe((items) => {
      this.bookingsDataSource.data = items
    })
    this.store.pipe(select(selectRentalEntities), untilDestroy(this)).subscribe((rentalsById) => {
      const ignoreRentals = lodash.compact(lodash.map(this.settings.ignoreRentals, (rId) => rentalsById[rId]))
      this.rentalsDataSource.data = ignoreRentals
    })

    this.guestService.fetch(this.settings.ignoreGuests).subscribe((items) => {
      this.guestsDataSource.data = items
    })
  }

  ngOnChanges() {
    this.refresh()
  }

  onSearchBookings(data: { term: string }) {
    this.bookingSearchTerm$.next(data.term)
  }

  onSearchGuests(data: { term: string }) {
    this.guestSearchTerm$.next(data.term)
  }

  onSelectAll(ctrl: UntypedFormControl, items: { id: string }[], checked: boolean) {
    if (checked) {
      ctrl.setValue(items.map((t) => t.id))
    } else {
      ctrl.setValue([])
    }
  }

  isAllSelected(ctrl: UntypedFormControl, items: { id: string }[]) {
    return ctrl.value?.length == items.length
  }

  onDeleteBooking(item: Inquiry) {
    this.amplitudeService.logEvent('ai-responder-ignore-list-delete-booking')
    const ignoreBookings = lodash.filter(this.settings.ignoreBookings, (id) => id != item.id)
    this.store.dispatch(
      UpdateAISettings({
        payload: { ignoreBookings },
        message: $localize`Booking Conversation deleted successfully from ignore list`,
      })
    )
  }

  onSaveBookings() {
    this.amplitudeService.logEvent('ai-responder-ignore-list-add-booking')
    const ids = this.bookingsCtrl.value || []
    const ignoreBookings = lodash.uniq([...this.settings.ignoreBookings, ...ids])
    this.store.dispatch(
      UpdateAISettings({
        payload: { ignoreBookings },
        message: $localize`Booking Conversation added successfully to ignore list`,
      })
    )
  }

  onDeleteGuest(item: Guest) {
    this.amplitudeService.logEvent('ai-responder-ignore-list-delete-guest')
    const ignoreGuests = lodash.filter(this.settings.ignoreGuests, (id) => id != item.id)
    this.store.dispatch(
      UpdateAISettings({
        payload: { ignoreGuests },
        message: $localize`Guest deleted successfully from ignore list`,
      })
    )
  }

  onSaveGuests() {
    this.amplitudeService.logEvent('ai-responder-ignore-list-add-guest')
    const ids = this.guestsCtrl.value || []
    const ignoreGuests = lodash.uniq([...this.settings.ignoreGuests, ...ids])
    this.store.dispatch(
      UpdateAISettings({
        payload: { ignoreGuests },
        message: $localize`Guest added successfully to ignore list`,
      })
    )
  }

  onDeleteGuestsRegexp(item: string) {
    this.amplitudeService.logEvent('ai-responder-ignore-list-delete-regexp')
    const ignoreGuestsRegexp = lodash.filter(this.settings.ignoreGuestsRegexp, (id) => id != item)

    this.store.dispatch(
      UpdateAISettings({
        payload: { ignoreGuestsRegexp },
        message: $localize`Regex pattern delete successfully from ignore list`,
      })
    )
  }

  onSaveGuestsRegexp() {
    this.amplitudeService.logEvent('ai-responder-ignore-list-add-regexp')
    const regexp = this.ignoreGuestsRegexpCtrl.value

    this.store.dispatch(
      UpdateAISettings({
        payload: { ignoreGuestsRegexp: lodash.uniq([...this.settings.ignoreGuestsRegexp, regexp]) },
        message: $localize`Regex pattern added successfully to ignore list`,
      })
    )
  }

  onDeleteRental(item: Rental) {
    this.amplitudeService.logEvent('ai-responder-ignore-list-delete-rental')
    const ignoreRentals = lodash.filter(this.settings.ignoreRentals, (id) => id != item.id)

    this.store.dispatch(
      UpdateAISettings({
        payload: { ignoreRentals },
        message: $localize`Rental deleted successfully from ignore list`,
      })
    )
  }

  onSaveRentals() {
    this.amplitudeService.logEvent('ai-responder-ignore-list-add-rental')
    const ids = this.rentalsCtrl.value || []
    const ignoreRentals = lodash.uniq([...this.settings.ignoreRentals, ...ids])

    this.store.dispatch(
      UpdateAISettings({
        payload: { ignoreRentals },
        message: $localize`Rental added successfully to ignore list`,
      })
    )
  }
}
